import React, { useState, useEffect, useRef } from "react";
import { Search, Plus, Loader2, Music4, Database, AlertTriangle, Star, Sparkles, Play } from "lucide-react";
import { searchYouTube, fetchFavorites, addFavorite, removeFavorite, recordLibraryUse } from "../../lib/api";
import { useKaraoke } from "../../store/KaraokeStore";
import { classifyVersionLabels } from "../../lib/labels";
import { LabelChips } from "./LabelChips";
import { toast } from "sonner";

const SearchPanel = ({ onPick, onPlayNow }) => {
  const { injectedSearch } = useKaraoke();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [ytResults, setYtResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ fromCache: false, fromStaleCache: false, forced: false, youtubeMessage: "", message: "" });
  const [favIds, setFavIds] = useState(new Set());
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const inflightKeyRef = useRef(null); // guards against identical simultaneous searches
  const lastNormalizedRef = useRef("");
  const lastQueryRef = useRef("");

  // Load the set of favorite videoIds so we can render ★ vs ☆
  useEffect(() => {
    fetchFavorites()
      .then((favs) => setFavIds(new Set(favs.map((f) => f.videoId))))
      .catch(() => {});
  }, []);

  const toggleFavorite = async (e, song) => {
    e.stopPropagation();
    const isFav = favIds.has(song.videoId);
    // optimistic update
    setFavIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(song.videoId);
      else next.add(song.videoId);
      return next;
    });
    try {
      if (isFav) {
        await removeFavorite(song.videoId);
        toast("Quitado de favoritos", { description: song.title.substring(0, 40) });
      } else {
        await addFavorite({ ...song, query: lastQueryRef.current || song.title });
        toast.success("★ Agregado a favoritos", { description: song.title.substring(0, 40) });
      }
    } catch (err) {
      // revert on error
      setFavIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(song.videoId);
        else next.delete(song.videoId);
        return next;
      });
      toast.error("No se pudo actualizar el favorito");
    }
  };

  const handlePick = (song) => {
    // Promote to the learned library (selecting a result == useful)
    recordLibraryUse(song, "youtube_search", lastQueryRef.current).catch(() => {});
    onPick(song);
  };

  const handlePlayNow = (e, song) => {
    e.stopPropagation();
    // Selecting a result is a useful signal even when playing it now.
    recordLibraryUse(song, "youtube_search", lastQueryRef.current).catch(() => {});
    onPlayNow && onPlayNow(song);
  };

  const normalize = (s) =>
    (s || "").trim().toLowerCase().replace(/\s+/g, " ");

  const doSearch = async (rawQuery, confirmed = false, excludeId = null) => {
    const q = (rawQuery || "").trim();
    if (!q) {
      setResults([]);
      setYtResults([]);
      setMeta({ fromCache: false, fromStaleCache: false, forced: false, youtubeMessage: "", message: "" });
      return;
    }
    // Búsquedas automáticas (debounce) exigen >= 4 caracteres.
    // Las confirmadas (Enter / botón Buscar) permiten consultas cortas (U2, AB).
    if (!confirmed && q.length < 4) {
      setResults([]);
      setYtResults([]);
      setMeta({ fromCache: false, fromStaleCache: false, forced: false, youtubeMessage: "", message: "" });
      return;
    }
    const normalized = normalize(q);
    if (!confirmed && normalized === lastNormalizedRef.current) return;

    // De-dupe identical simultaneous searches (e.g. Enter + click, double click)
    const reqKey = `${confirmed ? "F" : "A"}:${normalized}`;
    if (inflightKeyRef.current === reqKey) return;
    inflightKeyRef.current = reqKey;

    lastNormalizedRef.current = normalized;
    lastQueryRef.current = q;

    // Cancel previous in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      // El botón/Enter (confirmed) fuerza una consulta nueva a YouTube conservando lo local.
      const data = await searchYouTube(q, { maxResults: 12, signal: controller.signal, confirmed, forceYoutube: confirmed, excludeId: excludeId || undefined });
      setResults(data.results || []);
      setYtResults(data.youtubeResults || []);
      setMeta({
        fromCache: !!data.fromCache,
        fromStaleCache: !!data.fromStaleCache,
        fromLocalIndex: !!data.fromLocalIndex,
        forced: !!data.forced,
        youtubeMessage: data.youtubeMessage || "",
        message: data.message || "",
      });
    } catch (e) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      const msg =
        e?.response?.data?.detail ||
        (e?.response?.status === 429
          ? "Cuota de YouTube alcanzada. Intenta más tarde."
          : "Error al buscar en YouTube");
      toast.error(msg);
      setResults([]);
      setYtResults([]);
    } finally {
      if (inflightKeyRef.current === reqKey) inflightKeyRef.current = null;
      if (abortRef.current === controller) abortRef.current = null;
      setLoading(false);
    }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v.trim()) {
      setResults([]);
      setYtResults([]);
      setMeta({ fromCache: false, fromStaleCache: false, forced: false, youtubeMessage: "", message: "" });
      lastNormalizedRef.current = "";
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(v), 1200);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    lastNormalizedRef.current = ""; // force re-run on explicit Enter/click
    doSearch(query, true); // confirmed: allows short queries, ignores debounce
  };

  // DJ pressed "Buscar" on a search request / recovery: inject the text and run the search now
  useEffect(() => {
    if (injectedSearch && injectedSearch.query) {
      setQuery(injectedSearch.query);
      lastNormalizedRef.current = "";
      doSearch(injectedSearch.query, true, injectedSearch.excludeId || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injectedSearch?.nonce]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const renderRow = (r, idx) => {
    const isFav = favIds.has(r.videoId);
    return (
      <div
        key={r.videoId}
        data-testid={`search-result-${idx}`}
        className="w-full border-b border-border last:border-0 hover:bg-white/5 transition-colors p-3 flex items-start gap-3 group"
      >
        <button
          onClick={() => handlePick(r)}
          className="flex items-start gap-3 flex-1 min-w-0 text-left"
          data-testid={`pick-result-${idx}`}
        >
          <div className="relative shrink-0">
            <img
              src={r.thumbnail}
              alt={r.title}
              className="w-24 h-14 object-cover rounded-sm bg-muted"
              loading="lazy"
            />
            {r.duration && (
              <span className="absolute bottom-0.5 right-0.5 bg-black/85 text-white text-[10px] font-mono px-1 rounded-sm">
                {r.duration}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              {r.tier === "favorite" && (
                <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded px-1 py-0.5">
                  <Star className="w-2.5 h-2.5" fill="currentColor" /> Favorito
                </span>
              )}
              {r.tier === "library" && (
                <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-secondary bg-secondary/10 border border-secondary/30 rounded px-1 py-0.5">
                  <Sparkles className="w-2.5 h-2.5" /> Biblioteca
                </span>
              )}
            </div>
            <p className="text-sm font-semibold line-clamp-2 leading-snug group-hover:text-primary transition-colors mt-0.5">
              {r.title}
            </p>
            <LabelChips labels={r.labels || classifyVersionLabels(`${r.title} ${r.channelTitle}`)} content={r.content_labels} className="mt-1" />
            <p className="text-xs text-muted-foreground mt-1 truncate">{r.channelTitle}</p>
          </div>
        </button>
        <div className="flex flex-col items-center gap-2 shrink-0">
          <button
            onClick={(e) => toggleFavorite(e, r)}
            data-testid={`favorite-toggle-${idx}`}
            title={isFav ? "Quitar de favoritos" : "Marcar como favorito"}
            className={`transition-all active:scale-90 ${
              isFav ? "text-amber-400" : "text-muted-foreground hover:text-amber-400"
            }`}
          >
            <Star className="w-5 h-5" fill={isFav ? "currentColor" : "none"} />
          </button>
          <button
            onClick={(e) => handlePlayNow(e, r)}
            data-testid={`play-now-result-${idx}`}
            title="Reproducir ahora"
            className="text-muted-foreground hover:text-secondary transition-colors"
          >
            <Play className="w-4 h-4" fill="currentColor" />
          </button>
          <button
            onClick={() => handlePick(r)}
            data-testid={`add-result-${idx}`}
            title="Agregar a la cola"
            className="text-muted-foreground hover:text-primary transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const showMessage = meta.fromStaleCache && meta.message;

  return (
    <section
      className="col-span-1 flex flex-col h-full border border-border bg-surface rounded-lg overflow-hidden"
      data-testid="search-panel"
    >
      <div className="p-4 border-b border-border shrink-0">
        <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-muted-foreground mb-3">
          /// Buscador
        </p>
        <form onSubmit={onSubmit} className="relative flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={onChange}
              placeholder="Busca una canción..."
              data-testid="search-input"
              className="w-full bg-white/[0.04] border border-white/15 hover:border-white/25 focus:border-primary focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-md pl-10 pr-9 h-11 font-mono text-sm text-white placeholder:text-white/50 transition-all"
            />
            {loading && (
              <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-primary animate-spin" />
            )}
          </div>
          <button
            type="submit"
            data-testid="search-submit-button"
            className="shrink-0 h-11 px-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition-all flex items-center justify-center font-bold uppercase tracking-wide text-xs glow-pink"
            title="Buscar ahora"
          >
            <Search className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Buscar</span>
          </button>
        </form>
        <p className="text-[10px] font-mono text-muted-foreground/60 mt-2">
          + &ldquo;karaoke&rdquo; se añade automáticamente · resultados locales mientras escribes · BUSCAR consulta YouTube
        </p>
      </div>

      {showMessage && (
        <div
          className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/30 flex items-start gap-2"
          data-testid="search-stale-banner"
        >
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-200 leading-snug">{meta.message}</p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto" data-testid="search-results">
        {results.length === 0 && ytResults.length === 0 && !loading && (
          <div className="h-full flex flex-col items-center justify-center text-center p-6 text-muted-foreground">
            <Music4 className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">{meta.forced && meta.youtubeMessage ? meta.youtubeMessage : "Busca tu canción favorita"}</p>
            {!(meta.forced && meta.youtubeMessage) && <p className="text-xs mt-1 opacity-60">y agrégala a la cola</p>}
          </div>
        )}

        {/* Grupo LOCAL */}
        {results.length > 0 && (meta.forced || (meta.fromCache && !meta.fromStaleCache)) && (
          <div className="px-4 py-1.5 bg-secondary/[0.06] border-b border-secondary/20 flex items-center gap-2" data-testid="local-results-header">
            <Database className="w-3 h-3 text-secondary" />
            <p className="text-[10px] font-mono text-secondary/80 uppercase tracking-widest">
              {meta.forced || meta.fromLocalIndex ? "Coincidencias del caché local" : "Resultados desde caché"}
            </p>
          </div>
        )}
        {results.map((r, idx) => renderRow(r, idx))}

        {/* Grupo NUEVO de YouTube (solo en búsqueda forzada) */}
        {meta.forced && ytResults.length > 0 && (
          <div className="px-4 py-1.5 bg-primary/[0.07] border-y border-primary/25 flex items-center gap-2" data-testid="youtube-results-header">
            <Search className="w-3 h-3 text-primary" />
            <p className="text-[10px] font-mono text-primary/90 uppercase tracking-widest">
              Resultados nuevos de YouTube
            </p>
          </div>
        )}
        {meta.forced && ytResults.map((r, idx) => renderRow(r, results.length + idx))}

        {/* Nota discreta cuando la búsqueda forzada no trae nada nuevo */}
        {meta.forced && ytResults.length === 0 && meta.youtubeMessage && results.length > 0 && (
          <p className="px-4 py-2 text-[11px] font-mono text-muted-foreground/70" data-testid="youtube-no-new-note">
            {meta.youtubeMessage}
          </p>
        )}
      </div>
    </section>
  );
};

export default SearchPanel;
